import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@deepguide-ai/ui/dropdown-menu'
import { downloadBase64File } from '@/utils/download'
import { toast } from 'sonner'
import { RiskAssessmentAttachment } from '@/pages/risk/RiskAssessmentAttachment'
import { useState, useRef, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { Shield, Loader2, CheckCircle2, Download, MoreHorizontal, AlertCircle, UserCheck } from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { useSession } from '@/lib/auth-client'
import { TiptapAiEditor } from '@/tiptap/editor/ai-editor'
import { PageHeaderActions, PageHeaderBreadcrumb } from '@/components/layout/PageHeaderActions'
import { RiskAssessmentStatusBadge } from '@/pages/risk/RiskAssessmentStatusBadge'
import { usePageHeaderTitle } from '@/lib/page-header'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Button } from '@deepguide-ai/ui/button'
import PageLoading from '@/components/PageLoading'
import { renderToMarkdownReadable } from '@/tiptap/markdown/renderToMarkdown'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@deepguide-ai/ui/dialog'
import { Label } from '@deepguide-ai/ui/label'
import { Textarea } from '@deepguide-ai/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/risks_/assessments/$draftId'
)({
  component: RiskAssessmentDraftPage,
  staticData: { pageHeader: { title: 'Risk Assessment', pad: false } },
})

// Sentinel for the self-review option; resolves to the current user on submit.
const SELF_REVIEW_VALUE = '__self__'

function RiskAssessmentDraftPage() {
  const { workspaceId, draftId } = useParams({ strict: false }) as {
    workspaceId: string
    draftId: string
  }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const editorRef = useRef<any>(null)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false)
  const [showSubmitApproval, setShowSubmitApproval] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Submit for approval dialog state
  const [selectedReviewerId, setSelectedReviewerId] = useState('')
  const [approvalComments, setApprovalComments] = useState('')

  // Reviewer controls state
  const [reviewComments, setReviewComments] = useState('')

  const draftQuery = useQuery(
    trpc.riskRegister.getAssessmentDraft.queryOptions({ id: draftId })
  )

  const approvalQuery = useQuery(
    trpc.riskRegister.getApproval.queryOptions({ draftId })
  )

  const { data: session } = useSession()
  const currentUserId = session?.user?.id

  const eligibleReviewersQuery = useQuery(
    trpc.riskRegister.listEligibleReviewers.queryOptions()
  )

  const updateDraftMutation = useMutation(
    trpc.riskRegister.updateAssessmentDraft.mutationOptions()
  )
  const { mutateAsync: updateDraft } = updateDraftMutation

  const publishMutation = useMutation(
    trpc.riskRegister.publishAssessment.mutationOptions()
  )

  const submitForApprovalMutation = useMutation(
    trpc.riskRegister.submitForApproval.mutationOptions()
  )

  const reviewApprovalMutation = useMutation(
    trpc.riskRegister.reviewApproval.mutationOptions()
  )

  const draft = draftQuery.data
  const approval = approvalQuery.data
  const draftStatus = draft?.status ?? 'draft'
  const isPublished = draftStatus === 'published'
  const [downloading, setDownloading] = useState(false)
  const registerQuery = useQuery({ ...trpc.riskRegister.getAssessmentRegister.queryOptions({ draftId, page: 0 }), enabled: isPublished })
  const isPendingApproval = draftStatus === 'pending_approval'
  const isApproved = draftStatus === 'approved'
  const isChangesRequested = draftStatus === 'changes_requested'
  const isReadOnly = isPublished || isPendingApproval || isApproved
  // Hard gate: publishing is only offered once the assessment has been approved.
  const canPublish = isApproved

  const isCurrentUserReviewer = approval?.reviewerId === currentUserId

  // Dynamic header title — date-stamp the assessment so multiple drafts are
  // distinguishable in the header. Must run before any early return (hooks rule).
  const assessmentTitle = draft
    ? `Risk Assessment · ${new Date(draft.generatedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })}`
    : 'Risk Assessment'
  usePageHeaderTitle(assessmentTitle)

  const goBackToRegister = () =>
    navigate({ to: '/workspaces/$workspaceId/risks', params: { workspaceId } })

  const invalidateDraftQueries = () => {
    queryClient.invalidateQueries({ queryKey: trpc.riskRegister.listAssessmentDrafts.queryKey() })
    queryClient.invalidateQueries({ queryKey: trpc.riskRegister.getAssessmentDraft.queryKey({ id: draftId }) })
    queryClient.invalidateQueries({ queryKey: trpc.riskRegister.getApproval.queryKey({ draftId }) })
  }

  // Auto-save with debounce
  const handleEditorUpdate = useCallback(
    ({ editor }: { editor: any }) => {
      if (isReadOnly) return
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)

      saveTimerRef.current = setTimeout(async () => {
        setSaveStatus('saving')
        const tiptapJson = editor.getJSON()
        let markdown: string | undefined
        try {
          markdown = renderToMarkdownReadable(tiptapJson)
        } catch {
          // If markdown conversion fails, just save the JSON
        }

        await updateDraft({
          id: draftId,
          tiptapJson,
          content: markdown,
        })
        setSaveStatus('saved')
        setTimeout(() => setSaveStatus('idle'), 2000)
      }, 1500)
    },
    [draftId, isReadOnly, updateDraft]
  )

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  const persistEditorChanges = useCallback(async () => {
    if (!editorRef.current || isReadOnly) return
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }

    setSaveStatus('saving')
    const tiptapJson = editorRef.current.getJSON()
    let markdown: string | undefined
    try {
      markdown = renderToMarkdownReadable(tiptapJson)
    } catch {
      // proceed without markdown update
    }

    await updateDraft({
      id: draftId,
      tiptapJson,
      content: markdown,
    })

    setSaveStatus('saved')
    setTimeout(() => setSaveStatus('idle'), 2000)
  }, [draftId, isReadOnly, updateDraft])

  const handlePublish = async () => {
    await persistEditorChanges()

    try {
      await publishMutation.mutateAsync({ draftId })
    } catch {
      // The signed package may have committed before Test submission failed.
      return
    } finally {
      invalidateDraftQueries()
    }
    setShowPublishConfirm(false)

    // Navigate back to risk register
    navigate({
      to: '/workspaces/$workspaceId/risks',
      params: { workspaceId },
    })
  }

  const handleSubmitForApproval = async () => {
    const resolvedReviewerId =
      selectedReviewerId === SELF_REVIEW_VALUE ? (currentUserId ?? '') : selectedReviewerId
    if (!resolvedReviewerId) return
    await persistEditorChanges()
    await submitForApprovalMutation.mutateAsync({
      draftId,
      reviewerId: resolvedReviewerId,
      comments: approvalComments || undefined,
    })
    setShowSubmitApproval(false)
    setSelectedReviewerId('')
    setApprovalComments('')
    invalidateDraftQueries()
  }

  const handleReview = async (status: 'APPROVED' | 'REJECTED' | 'NEEDS_REVIEW') => {
    await reviewApprovalMutation.mutateAsync({
      draftId,
      status,
      comments: reviewComments || undefined,
    })
    setReviewComments('')
    invalidateDraftQueries()
  }

  if (draftQuery.isLoading) {
    return <PageLoading />
  }

  if (draftQuery.isError || !draft) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <p className="font-body text-[13px] text-text-subtle">Draft not found or could not be loaded.</p>
      </div>
    )
  }

  // Segregation of duties: only workspace admins/owner may review. Self-review is
  // offered only when the backend says the requester is the sole eligible reviewer.
  const reviewers = eligibleReviewersQuery.data?.reviewers ?? []
  const allowSelfReview = eligibleReviewersQuery.data?.allowSelfReview ?? false
  const otherReviewers = reviewers.filter((r) => r.userId !== currentUserId)
  const hasReviewerOptions = otherReviewers.length > 0 || allowSelfReview

  return (
    <>
      {/* Parent breadcrumb — renders left of the page title, chevron auto-inserted */}
      <PageHeaderBreadcrumb>
        <button
          type="button"
          onClick={goBackToRegister}
          className="font-heading text-[15px] font-medium text-text-subtle hover:text-cobalt-strong"
        >
          Risk Register
        </button>
      </PageHeaderBreadcrumb>
      <PageHeaderActions>
        {saveStatus === 'saving' && (
          <span className="flex h-8 items-center gap-1 font-body text-[11px] text-text-subtle">
            <Loader2 className="h-3 w-3 animate-spin" />
            Saving...
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className="flex h-8 items-center gap-1 font-body text-[11px] text-status-green">
            <CheckCircle2 className="h-3 w-3" />
            Saved
          </span>
        )}

          <RiskAssessmentStatusBadge status={draftStatus} />

          {/* Submit for Approval button — shown in draft or changes_requested state */}
          {(draftStatus === 'draft' || isChangesRequested) && (
            <Button
              size="sm"
              onClick={() => setShowSubmitApproval(true)}
            >
              <UserCheck className="h-3.5 w-3.5" />
              Submit for Approval
            </Button>
          )}

          {/* Publish button — shown only once the assessment has been approved */}
          {canPublish && (
            <Button
              size="sm"
              className="h-8 rounded-sm bg-primary px-3 text-[12px] text-primary-foreground hover:bg-primary/90"
              onClick={() => setShowPublishConfirm(true)}
              disabled={publishMutation.isPending}
            >
              <Shield className="h-3.5 w-3.5" />
              {publishMutation.isPending ? 'Publishing...' : 'Publish as Evidence'}
            </Button>
          )}

          {isPublished && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" aria-label="Assessment actions" disabled={downloading}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={registerQuery.isPending || registerQuery.isError} onSelect={async () => {
                  setDownloading(true)
                  try {
                    if (registerQuery.data) {
                      const file = await queryClient.fetchQuery(trpc.riskRegister.downloadAssessmentPackage.queryOptions({ draftId }))
                      downloadBase64File(file.base64, file.fileName, 'application/zip')
                    } else {
                      const file = await queryClient.fetchQuery(trpc.riskRegister.downloadAssessment.queryOptions({ draftId }))
                      const url = URL.createObjectURL(new Blob([file.content], { type: 'text/markdown' }))
                      const anchor = document.createElement('a')
                      anchor.href = url
                      anchor.download = file.fileName
                      anchor.click()
                      URL.revokeObjectURL(url)
                    }
                  } catch (error) { toast.error(error instanceof Error ? error.message : 'Could not export the assessment') }
                  finally { setDownloading(false) }
                }}>
                  <Download className="mr-2 h-4 w-4" />
                  {registerQuery.data ? 'Export evidence package' : 'Export assessment'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
      </PageHeaderActions>
      <div className="flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden">
        {/* Metadata bar — only shown when there is review context to display. */}
        {(isPendingApproval || isChangesRequested) && approval && (
          <div className="flex shrink-0 items-center gap-4 border-b border-border bg-surface-subtle px-6 py-2">
            {isPendingApproval && (
              <span className="font-body text-[11px] text-text-subtle">
                Awaiting review from {approval.reviewer?.name ?? approval.reviewer?.email}
              </span>
            )}
            {isChangesRequested && (
              <span className="flex items-center gap-1.5 font-body text-[11px] text-cobalt-strong">
                <AlertCircle className="h-3 w-3" />
                Changes requested by {approval.reviewer?.name ?? approval.reviewer?.email}
                {approval.reviewerComments && (
                  <span className="text-text-subtle">: &ldquo;{approval.reviewerComments}&rdquo;</span>
                )}
              </span>
            )}
          </div>
        )}

        {/* Reviewer controls — shown when current user is reviewer and status is pending_approval */}
        {isPendingApproval && isCurrentUserReviewer && (
          <div className="shrink-0 border-b border-border bg-card px-6 py-3">
            <Label className="font-body text-[11px] font-medium text-text-body mb-1.5 block">
              Reviewer Comments (optional)
            </Label>
            <Textarea
              value={reviewComments}
              onChange={(e) => setReviewComments(e.target.value)}
              placeholder="Add feedback or notes for the author..."
              className="h-[60px] resize-none rounded-sm border border-border-divider font-body text-[12px] text-text-secondary placeholder:text-text-placeholder"
            />
            {/* Actions on their own line beneath the comments, primary (Approve) rightmost */}
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-status-red/30 text-status-red hover:bg-status-red-bg"
                onClick={() => handleReview('REJECTED')}
                disabled={reviewApprovalMutation.isPending}
              >
                Reject
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-status-orange/30 text-status-orange hover:bg-status-orange-bg"
                onClick={() => handleReview('NEEDS_REVIEW')}
                disabled={reviewApprovalMutation.isPending}
              >
                Request Changes
              </Button>
              <Button
                size="sm"
                className="bg-status-green text-white hover:bg-status-green/90"
                onClick={() => handleReview('APPROVED')}
                disabled={reviewApprovalMutation.isPending}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Approve
              </Button>
            </div>
          </div>
        )}

        <RiskAssessmentAttachment key={draftId} draftId={draftId} published={isPublished} />

        {/* Editor content */}
        <ScrollArea className="min-h-0 flex-1" viewClassName="[&>div]:!block">
          <div className="flex min-h-full justify-center bg-muted px-20 py-10">
            <div className="w-full max-w-[816px] rounded-sm bg-card px-16 py-12 shadow-[0_2px_12px_rgba(0,0,0,0.06)] [&_.editor-container]:!pt-0 [&_.editor-content]:!bg-card [&_.drag-handle]:mr-0 [&_.drag-handle]:w-9 [&_.drag-handle]:translate-x-9 [&_.drag-handle]:pr-0">
              <TiptapAiEditor
                key={isReadOnly ? 'readonly' : 'editable'}
                content={draft.tiptapJson}
                readOnly={isReadOnly}
                onCreate={({ editor }) => {
                  editorRef.current = editor
                }}
                onUpdate={handleEditorUpdate}
              />
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
        <DialogContent className="max-w-md" closeClassName="top-4.5">
          <DialogHeader>
            <DialogTitle className="font-heading text-[16px] font-semibold text-foreground flex items-center gap-2">
              <Shield className="h-4 w-4 text-cobalt-strong" />
              Publish as Evidence
            </DialogTitle>
          </DialogHeader>
          <p className="font-body px-6 text-[13px] text-text-body mt-2">
            Publish this approved assessment and its frozen register as one versioned evidence package.
            Once published, the content cannot be edited.
          </p>
          <p className="font-body px-6 text-[11px] text-text-subtle mt-1">
            The assessment will be stored as a versioned artifact and available in your Evidence Vault.
          </p>
          <DialogFooter>
            {publishMutation.error && <p role='alert' className='text-sm text-destructive'>{publishMutation.error.message}</p>}
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-sm border-border-divider px-4 text-[12px]"
              onClick={() => setShowPublishConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-8 rounded-sm bg-primary px-4 text-[12px] text-primary-foreground hover:bg-primary/90"
              onClick={handlePublish}
              disabled={publishMutation.isPending || !canPublish}
            >
              {publishMutation.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Publishing...
                </>
              ) : (
                'Publish Evidence'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit for Approval Dialog */}
      <Dialog open={showSubmitApproval} onOpenChange={setShowSubmitApproval}>
        <DialogContent className="max-w-md" closeClassName="top-4.5">
          <DialogHeader>
            <DialogTitle className="font-heading text-[16px] font-semibold text-foreground flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-cobalt-strong" />
              Submit for Approval
            </DialogTitle>
          </DialogHeader>
          <p className="font-body  px-6  text-[13px] text-text-body mt-2">
            Select a reviewer to approve this risk assessment before publishing.
          </p>
          <div className="px-6 space-y-4">
            <div>
              <Label className="font-body text-[11px] font-medium text-text-body mb-1.5 block">
                Reviewer <span className="text-status-red">*</span>
              </Label>
              <Select value={selectedReviewerId} onValueChange={setSelectedReviewerId}>
                <SelectTrigger
                  fullWidth
                  className="h-9 rounded-sm border border-border-divider font-body text-[12px]"
                >
                  <SelectValue placeholder="Select a reviewer..." />
                </SelectTrigger>
                <SelectContent>
                  {eligibleReviewersQuery.isLoading ? (
                    <div className="font-body px-3 py-2 text-[11px] text-text-placeholder">
                      Loading reviewers...
                    </div>
                  ) : !hasReviewerOptions ? (
                    <div className="font-body px-3 py-2 text-[11px] text-text-placeholder">
                      No eligible reviewers
                    </div>
                  ) : (
                    <>
                      {otherReviewers.map((r) => (
                        <SelectItem key={r.userId} value={r.userId} className="font-body text-[12px]">
                          {r.name ?? r.email ?? r.userId}
                        </SelectItem>
                      ))}
                      {allowSelfReview && (
                        <SelectItem value={SELF_REVIEW_VALUE} className="font-body text-[12px]">
                          Myself (self-review)
                        </SelectItem>
                      )}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-body text-[11px] font-medium text-text-body mb-1.5 block">
                Comments (optional)
              </Label>
              <Textarea
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                placeholder="Add notes for the reviewer..."
                className="h-[80px] resize-none rounded-sm border border-border-divider font-body text-[12px] text-text-secondary placeholder:text-text-placeholder"
              />
            </div>
          </div>
          <DialogFooter>
            {submitForApprovalMutation.error && <p role='alert' className='text-sm text-destructive'>{submitForApprovalMutation.error.message}</p>}
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-sm border-border-divider px-4 text-[12px]"
              onClick={() => {
                setShowSubmitApproval(false)
                setSelectedReviewerId('')
                setApprovalComments('')
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-8 rounded-sm bg-primary px-4 text-[12px] text-primary-foreground hover:bg-primary/90"
              onClick={handleSubmitForApproval}
              disabled={!selectedReviewerId || submitForApprovalMutation.isPending}
            >
              {submitForApprovalMutation.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit for Approval'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
